import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageContainer, PageHeader } from '@/components/layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  ArrowLeft,
  Lock,
} from 'lucide-react';
import { PASSWORD_REGEX, PASSWORD_RULE_MESSAGE, getPasswordChecks } from '@/lib/validation';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { Spinner } from '@/components/ui/spinner';

const API_BASE_URL = `${BASE}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ChangePasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isSocialLoginUser =
    user?.loginType === 'GOOGLE' || user?.loginType === 'NAVER' || user?.loginType === 'KAKAO';

  const loginTypeLabelMap: Record<string, string> = {
    GOOGLE: 'Google',
    NAVER: '네이버',
    KAKAO: '카카오',
  };
  const socialLoginLabel = user?.loginType ? loginTypeLabelMap[user.loginType] : null;

  const passwordChecks = getPasswordChecks(form.newPassword);
  const passwordValid = PASSWORD_REGEX.test(form.newPassword);
  const confirmMatch =
    form.confirmPassword.length > 0 && form.newPassword === form.confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isSocialLoginUser) {
      setError(`${socialLoginLabel || '소셜'} 계정으로 로그인한 사용자는 비밀번호를 변경할 수 없습니다.`);
      return;
    }
    if (!form.currentPassword) {
      setError('현재 비밀번호를 입력해주세요.');
      return;
    }
    if (!passwordValid) {
      setError(PASSWORD_RULE_MESSAGE);
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/change-password`,
        { currentPassword: form.currentPassword, newPassword: form.newPassword },
        { headers: getAuthHeaders() },
      );
      if (response.data.success) {
        setSuccess(true);
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => navigate('/dashboard/profile/edit'), 2000);
      } else {
        setError(response.data.message || '비밀번호 변경 중 오류가 발생했습니다.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer maxWidth="full">
      <div className="flex items-start gap-3">
        <Link to="/dashboard/profile/edit" className="mt-1 text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <PageHeader 
            title="비밀번호 변경" 
            description="새로운 비밀번호를 입력해주세요"
          />
        </div>
      </div>

      {/* 알림 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-700">
            비밀번호가 변경되었습니다. 잠시 후 프로필 페이지로 이동합니다.
          </AlertDescription>
        </Alert>
      )}

      {/* 소셜 계정 안내 */}
      {isSocialLoginUser && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          <AlertDescription className="text-orange-700 text-sm">
            {socialLoginLabel} 계정으로 로그인한 사용자는 비밀번호를 변경할 수 없습니다.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">비밀번호 변경</CardTitle>
          <CardDescription>새로운 비밀번호를 입력해주세요</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* 현재 비밀번호 */}
            <div className="space-y-1.5">
              <Label htmlFor="currentPassword">
                현재 비밀번호 <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrent ? 'text' : 'password'}
                  value={form.currentPassword}
                  onChange={e => setForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  disabled={saving || isSocialLoginUser}
                  className="pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 새 비밀번호 */}
            <div className="space-y-1.5">
              <Label htmlFor="newPassword">
                새 비밀번호 <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNew ? 'text' : 'password'}
                  value={form.newPassword}
                  onChange={e => setForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  disabled={saving || isSocialLoginUser}
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* 실시간 조건 체크 */}
              <ul className="mt-2 space-y-1">
                {passwordChecks.map(({ label, ok }) => (
                  <li
                    key={label}
                    className={`text-xs flex items-center gap-1 ${
                      form.newPassword.length === 0
                        ? 'text-gray-400'
                        : ok
                        ? 'text-green-600'
                        : 'text-red-500'
                    }`}
                  >
                    {form.newPassword.length === 0 ? '•' : ok ? '✓' : '✗'} {label}
                  </li>
                ))}
              </ul>
            </div>

            {/* 새 비밀번호 확인 */}
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">
                새 비밀번호 확인 <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={e => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  disabled={saving || isSocialLoginUser}
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.confirmPassword && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${confirmMatch ? 'text-green-600' : 'text-red-500'}`}>
                  {confirmMatch
                    ? <><CheckCircle2 className="w-3.5 h-3.5" /> 비밀번호가 일치합니다</>
                    : <>✗ 비밀번호가 일치하지 않습니다</>}
                </p>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 pt-2 border-t">
              <Button
                type="submit"
                disabled={saving || isSocialLoginUser || !passwordValid || !confirmMatch}
                className="flex-1"
              >
                {saving && <Spinner size="sm" className="mr-1.5" />}
                변경
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={saving}
                onClick={() => navigate('/dashboard/profile/edit')}
              >
                취소
              </Button>
            </div>

          </form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
